import apiClient from '@/api/apiClient';
import { handleError, getAuthData, prepareModel, validateId } from '../../utils/storeHelpers';

export default {
  namespaced: true,
  state: {
    schema: {},
    modelForm: {},
    dataCrud: [],
    isNewRecord: true,
    status: '',
  },

  getters: {
    schema: (state) => state.schema,
    modelForm: (state) => state.modelForm,
    dataCrud: (state) => state.dataCrud,
    isNewRecord: (state) => state.isNewRecord,
    status: (state) => state.status,
  },

  mutations: {
    SET_SCHEMA(state, schema) {
      state.schema = schema;
    },
    SET_RECORDS(state, { recordsData }) {
      state.modelForm = typeof recordsData?.modelForm === 'object' ? recordsData.modelForm : {};
      state.dataCrud = Array.isArray(recordsData?.dataCrud) ? recordsData.dataCrud : [];
      state.isNewRecord = Boolean(recordsData?.isNewRecord);
    },
    SET_STATUS(state, status) {
      state.status = status;
    },
  },

  actions: {
    async fetchSchema({ commit }) {
      try {
        commit('SET_STATUS', 'loading');
        const responses = await Promise.all([
          apiClient.filiationPersonalSchema(),
          apiClient.catISO3366_1(),
          apiClient.catDepartments(),
          apiClient.catInstructionYears(),
          apiClient.catInstructionLevels(),
          apiClient.catEduInstitutions(),
          apiClient.catCareers(),
          apiClient.catOccupation(),
          apiClient.catMaritalStatus(),
          apiClient.catReligion(),
          apiClient.catInstitutions(),
          apiClient.catHealthServices(),
          apiClient.catBond(),
        ]);
        const [
          schemaRes,
          catISO3366_1,
          catDepartments,
          catInstructionYears,
          catInstructionLevels,
          catEduInstitutions,
          catCareers,
          catOccupation,
          catMaritalStatus,
          catReligion,
          catInstitutions,
          catHealthServices,
          catBond,
        ] = responses.map((res) => res?.data ?? {});

        const id = 'I';
        const resCat11 = await apiClient.catLevelDescription(id);
        const catLevelDescription = resCat11.data.data;

        if (!schemaRes.formCrud) throw new Error('Estructura de schema inválida.');
        if (!schemaRes.schemaForm) throw new Error('Estructura de schema inválida.');

        const onlyPeru = catISO3366_1.data.filter(
          c => c.ISO3366_1_alfa2 === "PE"
        );

        const schema = structuredClone(schemaRes);
        schema.schemaForm.group1.schema.filiation_residence_iso3366.items = onlyPeru;
        schema.schemaForm.group1.schema.filiation_residence_department.items =
          catDepartments.data ?? [];
        schema.schemaForm.group2.schema.instruction_year_code.items =
          catInstructionYears.data ?? [];
        schema.schemaForm.group2.schema.instruction_level_code.items =
          catInstructionLevels.data ?? [];
        schema.schemaForm.group2.schema.edu_institution_code.items = catEduInstitutions.data ?? [];
        schema.schemaForm.group2.schema.careers_code.items = catCareers.data ?? [];
        schema.schemaForm.group2.schema.occupation_id.items = catOccupation.data ?? [];
        schema.schemaForm.group2.schema.marital_status_marital_status_id.items =
          catMaritalStatus.data ?? [];
        schema.schemaForm.group2.schema.socioeconomic_level_description_id.items =
          catLevelDescription;
        schema.schemaForm.group2.schema.religion_religion_id.items = catReligion.data ?? [];
        schema.schemaForm.group3.schema.institution_code.items = catInstitutions.data ?? [];
        schema.schemaForm.group3.schema.health_service_code.items = catHealthServices.data ?? [];
        schema.schemaForm.group3.schema.institution_referral_code.items =
          catInstitutions.data ?? [];
        schema.schemaForm.group3.schema.health_service_referral_code.items =
          catHealthServices.data ?? [];
        schema.formCrud.bond.items = catBond.data ?? [];

        commit('SET_SCHEMA', schema);
      } catch (error) {
        handleError(error, commit);
      }
    },

    async fetchRecords({ commit, rootState }) {
      try {
        const { clinicalHistories_id, institutions_profes_id } = getAuthData(rootState);
        const res = await apiClient.modelFiliationPersonal(clinicalHistories_id, institutions_profes_id);
        const recordsData = res?.data?.data ?? [];

        commit('SET_RECORDS', { recordsData });
        commit('SET_STATUS', 'loaded');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async createRecord({ commit, rootState }, { model }) {
      try {
        commit('SET_STATUS', 'create');
        const preparedModel = prepareModel(rootState, model);
        await apiClient.createFiliationPersonal(preparedModel);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async updateRecord({ commit, rootState }, { model }) {
      try {
        commit('SET_STATUS', 'update');
        const { clinicalHistories_id } = getAuthData(rootState);
        const preparedModel = prepareModel(rootState, model);
        await apiClient.updateFiliationPersonal(clinicalHistories_id, preparedModel);
        commit('SET_STATUS', 'success');
      } catch (error) {
        console.error('[App Error]:', error.message || 'Error desconocido');
        throw error;
      }
    },

    async removeRecord({ commit, rootState }, { id }) {
      try {
        commit('SET_STATUS', 'remove');
        const { clinicalHistories_id, institutions_profes_id } = getAuthData(rootState);
        validateId(id);
        
        await apiClient.deleteFiliationPersonal(id, clinicalHistories_id, institutions_profes_id);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },
  },
};
